import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useOpenState from '../../../../hooks/useOpenState';
import AgyleDialog from '../../../../components/modal/agyleDialog';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { setSnackbar } from '../../../../store/reducers/globals';
import { find, includes } from 'lodash';
import { prescriptionPatientApi } from '../../../../app/apiPrescription';
import AgyleLoadingSpinner from '../../../../components/other/agyleLoadingSpinner';
import { cis2token } from '../../../../store/reducers/cis2';
import InformationBox from '../../../../components/other/informationBox';
import * as React from 'react';
import { getConfig } from '../../../../util/configUtil';

const MODAL_TYPE = {
    LOADING: 'loading',
    NO_NHS: 'noNHS',
    ERROR: 'error',
    PT_CANNOT_VERIFIED: 'ptCanNotVerified',
    PT_NOT_VERIFIED: 'ptNotVerified',
};

const PrescriptionPatientCheck = ({ patient, closeForm }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const controller = new AbortController();

    const token = useSelector(cis2token);

    const [modalType, setModalType] = useState(MODAL_TYPE.LOADING);
    const [isModalOpen, openModal, closeModal] = useOpenState(false);
    const showButtonOn = [MODAL_TYPE.LOADING, MODAL_TYPE.NO_NHS, MODAL_TYPE.ERROR, MODAL_TYPE.PT_NOT_VERIFIED];

    useEffect(() => {
        return () => {
            controller.abort();
        };
    }, []);

    useEffect(() => {
        openModal();
        if (!patient) {
            dispatch(setSnackbar({ type: 'error', message: t('Patient data is not available') }));
            closeForm();
            return;
        }

        //
        let nhs = find(patient?.patient?.patientNumbers, (numb) => String(numb?.type).toLowerCase() === 'NhsNumber'.toLowerCase())?.number;

        if (!nhs) {
            setModalType(MODAL_TYPE.NO_NHS);
            return;
        }

        const demoNhs = getConfig('prescriptionApi.demoNhs');
        if (demoNhs) {
            nhs = demoNhs;
        }
        // TODO change this for NHS number from patient. This dummy NHS is for sandbox
        // nhs = '9000000009'; // Patient exists                       HTTP Status 200 with patient data in response
        // nhs = '9000000025'; // Sensitive patient exists             HTTP Status 200 with patient data in response with the restricted data removed
        // nhs = '9000000033'; // Patient exists with minimal data     HTTP Status 200 with patient data in response, there will be very little data so can be used as an example of a patient with bad data quality
        // nhs = '9111231130'; // Patient does not exist               HTTP Status 404 with problem description
        // nhs = '9000000000'; // Invalid NHS number                   HTTP Status 400 with problem description

        prescriptionPatientApi
            .getPatientFromPDS({ nhsNumber: nhs, cis2token: token, signal: controller.signal })
            .then((res) => {
                if (!res) {
                    setModalType(MODAL_TYPE.ERROR);
                    dispatch(setSnackbar({ type: 'error', message: t('Failed to verify patient details') }));
                    return;
                }
                const schema = 'NHSNumberVerificationStatus';
                const identifier = find(res?.identifier, (id) => id?.value === nhs);
                const extension = find(identifier?.extension, (ext) => ext?.url?.includes(schema));
                const coding = find(extension?.valueCodeableConcept?.coding, (code) => code?.system?.includes(schema));
                if (!identifier || !extension || !coding) {
                    setModalType(MODAL_TYPE.PT_CANNOT_VERIFIED);
                    return;
                }
                if (coding?.code !== '01') {
                    setModalType(MODAL_TYPE.PT_NOT_VERIFIED);
                    return;
                }
                closeModal();
            })
            .catch((error) => {
                if (controller.signal.aborted) return;

                setModalType(MODAL_TYPE.ERROR);
                dispatch(setSnackbar({ type: 'error', message: t('Failed to verify patient details') }));
                console.error('error', error);
            });

        return () => {
            controller.abort();
        };
    }, [patient]);

    const renderMessage = useCallback(() => {
        switch (modalType) {
            case MODAL_TYPE.LOADING:
                return (
                    <>
                        <AgyleLoadingSpinner disableText spinnerSize={60} />
                        <InformationBox type={'info'}>{t('Verifying patient details. Please wait...')}</InformationBox>
                    </>
                );
            case MODAL_TYPE.NO_NHS:
                return (
                    <InformationBox type={'error'}>
                        {t('Patient does not have a valid NHS number. Please update it to proceed.')}
                    </InformationBox>
                );
            case MODAL_TYPE.ERROR:
                return (
                    <InformationBox type={'error'}>
                        {t('Failed to verify patient details. Please check the information and try again.')}
                    </InformationBox>
                );
            case MODAL_TYPE.PT_CANNOT_VERIFIED:
                return <InformationBox type={'warning'}>{t('Patient could not be verified against NHS records.')}</InformationBox>;
            case MODAL_TYPE.PT_NOT_VERIFIED:
                return <InformationBox type={'error'}>{t('Patient NHS number could not be verified.')}</InformationBox>;
            default:
                break;
        }
        return null;
    }, [modalType]);

    if (!isModalOpen) return null;

    return (
        <AgyleDialog
            open={isModalOpen}
            width="sm"
            disableTitle
            disableActions
            disablePortal
            fullWidth={true}
            closeButtonOnHeader
            disableSave
            blur={10}
        >
            <Box style={{ width: '100%' }}>
                {/*<AgyleLoadingSpinner disableText spinnerSize={60} />*/}
                <Typography variant="h5" fontWeight={600} align="center">
                    {t('Verifying patient')}
                </Typography>
                <Typography variant="body1" fontWeight={600} align="center" sx={{ my: 1 }}>
                    {renderMessage()}
                </Typography>
                {showButtonOn.includes(modalType) && (
                    <AgyleButton variant="contained" fullWidth onClick={closeForm} style={{ float: 'right', marginTop: 10 }}>
                        {t('Close')}
                    </AgyleButton>
                )}
            </Box>
        </AgyleDialog>
    );
};

export default PrescriptionPatientCheck;
